import { useMemo, useCallback } from 'react';
import { TemplateEngine, TemplateProcessingOptions } from '../services';
import { HTMLPosts } from '../types';

interface UseTemplateEngineConfig {
  defaultOptions?: TemplateProcessingOptions;
}

interface UseTemplateEngineReturn {
  processTemplate: (template: string, data: any, options?: TemplateProcessingOptions) => string;
  processMultipleItems: (
    template: string,
    items: any[],
    options?: TemplateProcessingOptions
  ) => HTMLPosts;
  extractTokens: (template: string) => string[];
  validateTemplate: (template: string, data: any) => { valid: boolean; missingTokens: string[] };
  previewTokenReplacements: (template: string, data: any) => { [token: string]: any };
}

export const useTemplateEngine = (
  config: UseTemplateEngineConfig = {}
): UseTemplateEngineReturn => {
  // Create a memoized instance of the template engine
  const engine = useMemo(() => new TemplateEngine(), []);

  // Memoized process template function
  const processTemplate = useCallback(
    (template: string, data: any, options: TemplateProcessingOptions = {}): string => {
      const finalOptions = { ...config.defaultOptions, ...options };
      return engine.processTemplate(template, data, finalOptions);
    },
    [engine, config.defaultOptions]
  );

  // Memoized process multiple items function
  const processMultipleItems = useCallback(
    (template: string, items: any[], options: TemplateProcessingOptions = {}): HTMLPosts => {
      const finalOptions = { ...config.defaultOptions, ...options };
      return engine.processMultipleItems(template, items, finalOptions);
    },
    [engine, config.defaultOptions]
  );

  // Extract tokens function
  const extractTokens = useCallback(
    (template: string): string[] => {
      return engine.extractTokens(template);
    },
    [engine]
  );

  // Validate template function
  const validateTemplate = useCallback(
    (template: string, data: any) => {
      return engine.validateTemplate(template, data);
    },
    [engine]
  );

  // Preview token replacements function
  const previewTokenReplacements = useCallback(
    (template: string, data: any) => {
      return engine.previewTokenReplacements(template, data);
    },
    [engine]
  );

  return {
    processTemplate,
    processMultipleItems,
    extractTokens,
    validateTemplate,
    previewTokenReplacements,
  };
};
